import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { PROTECTED_PATHS } from '../../navigation/paths';
import { ExitIcon } from '../svgs/ExitIcon';
import useAuth from '@/features/auth/hooks/useAuth';
import { usePageHeaderValue } from '@/context/PageHeaderContext';
import LogoutConfirmModal from './LogoutConfirmModal';

type NavBarProps = {
  /* Provided only by the layout that actually renders a sidebar; without it the toggle is
     omitted (the standalone children page has no sidebar to collapse). */
  onToggleSidebar?: () => void;
  sidebarCollapsed?: boolean;
};

export default function NavBar({ onToggleSidebar, sidebarCollapsed = false }: NavBarProps) {
  const navigate = useNavigate();
  const { parent, logout } = useAuth();
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  /* Mobile pages hand their title (and back action) to the navbar rather than spending a
     block of the viewport on a heading of their own. */
  const { title, onBack } = usePageHeaderValue();

  const navigateToChildren = () => {
    navigate(PROTECTED_PATHS.children);
  };

  /* The logo now lives at the top of the sidebar, so the navbar only carries it where the
     sidebar can't: on mobile, on pages that render no sidebar, and while the rail is
     collapsed (too narrow for the wordmark). */
  const sidebarShowsLogo = Boolean(onToggleSidebar) && !sidebarCollapsed;

  return (
    <div className="shrink-0 top-0 w-full h-[var(--navbar-height)] bg-linear-to-l from-[#6955f8] to-[#6ba4f6]">
      <div className="flex justify-between items-center w-full h-full gap-2 px-3 lg:px-4">
        {/* Inline-start — the right edge in RTL, up against the sidebar */}
        <div className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
          {onBack && (
            <button
              onClick={onBack}
              title="الرجوع"
              aria-label="الرجوع"
              className="lg:hidden shrink-0 flex justify-center items-center p-1.5 aspect-square rounded-full bg-white/15 text-white cursor-pointer transition-all duration-200 active:scale-95"
            >
              <ChevronRight size={20} />
            </button>
          )}

          {title && (
            <h1 className="lg:hidden text-white text-base font-samim-bold truncate min-w-0">
              {title}
            </h1>
          )}

          {onToggleSidebar && (
            <button
              onClick={onToggleSidebar}
              title={sidebarCollapsed ? 'توسيع القائمة الجانبية' : 'طي القائمة الجانبية'}
              aria-label={sidebarCollapsed ? 'توسيع القائمة الجانبية' : 'طي القائمة الجانبية'}
              aria-expanded={!sidebarCollapsed}
              className="hidden lg:flex shrink-0 justify-center items-center p-2 aspect-square rounded-full bg-white/15 text-white cursor-pointer transition-all duration-200 hover:bg-white/25 active:scale-95"
            >
              {sidebarCollapsed ? <PanelRightOpen size={22} /> : <PanelRightClose size={22} />}
            </button>
          )}
        </div>

        {/* Inline-end — the left edge in RTL: the parent's name, with logout to its left */}
        <button
          onClick={() => setIsLogoutConfirmOpen(true)}
          title="تسجيل خروج"
          className="flex items-center gap-2 shrink-0 cursor-pointer transition-transform duration-200 active:scale-95 group"
        >
          <span className="text-white text-lg lg:text-xl whitespace-nowrap font-samim-bold hidden min-[768px]:block">
            {parent?.fullName}
          </span>
          <div className="flex justify-center items-center p-1.5 aspect-square rounded-full bg-white/15 transition-colors duration-200 group-hover:bg-white/25">
            <ExitIcon color="#fff" />
          </div>
        </button>
      </div>

      <LogoutConfirmModal
        isOpen={isLogoutConfirmOpen}
        onConfirm={() => {
          setIsLogoutConfirmOpen(false);
          logout();
        }}
        onCancel={() => setIsLogoutConfirmOpen(false)}
      />
    </div>
  );
}
